import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import 'dayjs/locale/pl';

import OwnerLayout from '../OwnerLayout';
import { OwnerContext } from '../../../context/OwnerContext';
import { OwnerCalendarProvider, useOwnerCalendar } from '../../../context/OwnerCalendarContext';
import { useOwnerCalendarData } from '../../../hooks/calendar/useOwnerCalendarData';
import { useOwnerBookingLogic } from '../../../hooks/calendar/useOwnerBookingLogic';

import OwnerCalendarControls from './OwnerCalendarControls';
import OwnerCalendarGrid from './OwnerCalendarGrid';
import AdditionalInfoModal from './AdditionalInfoModal';
import DeleteConfirmationModal from '../../trainer/DeleteConfirmationModal';
import MobileOwnerCalendar from './MobileOwnerCalendar';

import { OWNER_CALENDAR_CONSTANTS } from '../../../utils/calendar/constants';
import { formatMultipleBookingSummary } from '../../../utils/calendar/ownerHelpers';
import { getServiceDuration } from '../../../utils/calendar/durationService';
import '../../../styles/OwnerCalendar.css';

dayjs.locale('pl');
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);

const OwnerCalendarContent = () => {
    const { owner } = useContext(OwnerContext);
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(false);

    const {
        currentWeekStart,
        selectedTrainer,
        selectedDogId,
        selectedServiceId,
        selectedSlots,
        bookingMode,
        schoolSettings,
        additionalInfoModalOpen,
        deleteModalOpen,
        trainingToDelete,
        goToPreviousWeek,
        goToNextWeek,
        getWeekLabel,
        selectAllSlotsForDay,
        deselectAllSlotsForDay,
        clearSelectedSlots,
        toggleSlotSelection,
        enableMultipleBookingMode,
        disableMultipleBookingMode,
        openAdditionalInfoModal,
        closeAdditionalInfoModal,
        openDeleteModal,
        closeDeleteModal,
    } = useOwnerCalendar();

    const {
        trainers,
        ownerDogs,
        services,
        workingHours,
        trainings,
        slotBookings,
        lockedSlots,
        trainingLocks,
        confirmedTrainings,
        groupTrainings,
        isLoading,
        bookSingleTraining,
        bookMultipleTrainings,
        cancelTraining,
        refreshCalendar,
    } = useOwnerCalendarData({
        ownerId: owner?.id,
        schoolId: owner?.schoolId,
    });

    const {
        getFreeSlotsForDay,
        validateMultipleBooking,
    } = useOwnerBookingLogic({
        trainings,
        trainingLocks,
        confirmedTrainings,
        slotBookings,
        workingHours,
    });

    const selectedService = services.find(s => s.id === selectedServiceId);
    const serviceDurationMinutes = selectedService ? getServiceDuration(selectedService) : 60;

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1034);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (schoolSettings?.allowMultipleBookings) {
            enableMultipleBookingMode();
        } else {
            disableMultipleBookingMode();
        }
    }, [schoolSettings, enableMultipleBookingMode, disableMultipleBookingMode]);

    const handleBookSlot = useCallback(async (slotTime) => {
        if (!selectedDogId) {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.NO_DOG);
            return;
        }

        const result = await bookSingleTraining(slotTime, selectedServiceId, serviceDurationMinutes);

        if (result.success) {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.BOOKING_SUCCESS);
        } else {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.BOOKING_FAILED);
        }
    }, [selectedDogId, selectedServiceId, serviceDurationMinutes, bookSingleTraining]);

    const handleMultipleBookingConfirm = useCallback(async (additionalInfo = '') => {
        const validation = validateMultipleBooking(selectedSlots);

        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        const result = await bookMultipleTrainings(selectedSlots, selectedServiceId, additionalInfo, serviceDurationMinutes);

        if (result.success) {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.MULTIPLE_BOOKING_SUCCESS);
            clearSelectedSlots();
            closeAdditionalInfoModal();
        } else {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.MULTIPLE_BOOKING_FAILED);
        }
    }, [selectedSlots, selectedServiceId, serviceDurationMinutes, validateMultipleBooking, bookMultipleTrainings, clearSelectedSlots, closeAdditionalInfoModal]);

    const handleCancelTraining = useCallback((training) => {
        openDeleteModal(training);
    }, [openDeleteModal]);

    const handleConfirmCancellation = useCallback(async () => {
        if (!trainingToDelete) return;

        const result = await cancelTraining(trainingToDelete.id);

        if (result.success) {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.CANCEL_SUCCESS);
            closeDeleteModal();
        } else {
            alert(OWNER_CALENDAR_CONSTANTS.MESSAGES.CANCEL_FAILED);
        }
    }, [trainingToDelete, cancelTraining, closeDeleteModal]);

    const handleDayCheckboxChange = useCallback((dayDate, checked) => {
        const freeSlots = getFreeSlotsForDay(dayDate, serviceDurationMinutes);

        if (checked) {
            selectAllSlotsForDay(freeSlots);
        } else {
            deselectAllSlotsForDay(freeSlots);
        }
    }, [getFreeSlotsForDay, serviceDurationMinutes, selectAllSlotsForDay, deselectAllSlotsForDay]);

    const handleMultipleBookingClick = useCallback(() => {
        if (selectedSlots.length === 0) return;

        const summary = formatMultipleBookingSummary(
            selectedSlots,
            services.find(s => s.id === selectedServiceId)
        );

        if (window.confirm(summary + '\n\nCzy chcesz kontynuować?')) {
            openAdditionalInfoModal();
        }
    }, [selectedSlots, selectedServiceId, services, openAdditionalInfoModal]);

    // Placeholder component for when selections are not complete
    const SelectionPlaceholder = ({ isMobileView }) => (
        <div className={isMobileView ? "owner-mobile-calendar-placeholder" : "owner-calendar-placeholder"}>
            <div className={isMobileView ? "owner-mobile-selection-required" : "owner-selection-required"}>
                <div className={isMobileView ? "owner-mobile-selection-message" : "owner-selection-message"}>
                    Wybierz trenera, pupila i rodzaj treningu,
                    {isMobileView ? ' ' : <br />}
                    aby wyświetlić kalendarz
                </div>
                <div className={isMobileView ? "owner-mobile-selection-status" : "owner-selection-status"}>
                    <div className={`status-pill ${selectedTrainer ? 'completed' : ''}`}>
                        Trener
                    </div>
                    <div className={`status-pill ${selectedDogId ? 'completed' : ''}`}>
                        Pupil
                    </div>
                    <div className={`status-pill ${selectedServiceId ? 'completed' : ''}`}>
                        Rodzaj treningu
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <OwnerLayout>
            <div className="owner-calendar-container">
                <div className="owner-calendar-header">
                    <h1 className="owner-calendar-title">Zapisy na treningi</h1>
                </div>

                <OwnerCalendarControls
                    trainers={trainers}
                    ownerDogs={ownerDogs}
                    services={services}
                    onMultipleBookingClick={handleMultipleBookingClick}
                />

                {isMobile ? (
                    selectedTrainer && selectedDogId && selectedServiceId ? (
                        <MobileOwnerCalendar
                            currentWeekStart={currentWeekStart}
                            selectedTrainer={selectedTrainer}
                            openingHours={workingHours}
                            trainings={trainings}
                            trainingLocks={trainingLocks}
                            confirmedTrainings={confirmedTrainings}
                            slotBookings={slotBookings}
                            schoolSettings={schoolSettings}
                            selectedDogId={selectedDogId}
                            selectedServiceId={selectedServiceId}
                            selectedService={selectedService}
                            selectedSlots={selectedSlots}
                            onPrevWeek={goToPreviousWeek}
                            onNextWeek={goToNextWeek}
                            onBookSlot={handleBookSlot}
                            onToggleSlot={toggleSlotSelection}
                            onDeclineTraining={handleCancelTraining}
                        />
                    ) : (
                        <SelectionPlaceholder isMobileView={true} />
                    )
                ) : (
                    <>
                        {selectedTrainer && selectedDogId && selectedServiceId ? (
                            <>
                                <div className="owner-calendar-navigation">
                                    <div className="owner-week-navigation-left">
                                        <button onClick={goToPreviousWeek}>‹</button>
                                    </div>
                                    <div className="owner-current-week-info">
                                        {getWeekLabel()}
                                    </div>
                                    <div className="owner-week-navigation-right">
                                        <button onClick={goToNextWeek}>›</button>
                                    </div>
                                </div>

                                {isLoading ? (
                                    <div className="owner-calendar-loading">
                                        Ładowanie kalendarza...
                                    </div>
                                ) : (
                                    <OwnerCalendarGrid
                                        workingHours={workingHours}
                                        trainings={trainings}
                                        trainingLocks={trainingLocks}
                                        confirmedTrainings={confirmedTrainings}
                                        slotBookings={slotBookings}
                                        selectedService={selectedService}
                                        onSlotClick={handleBookSlot}
                                        onCancelTraining={handleCancelTraining}
                                        onDayCheckboxChange={handleDayCheckboxChange}
                                    />
                                )}
                            </>
                        ) : (
                            <SelectionPlaceholder isMobileView={false} />
                        )}
                    </>
                )}

                <AdditionalInfoModal
                    isOpen={additionalInfoModalOpen}
                    onClose={closeAdditionalInfoModal}
                    onConfirm={handleMultipleBookingConfirm}
                />

                <DeleteConfirmationModal
                    isOpen={deleteModalOpen}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmCancellation}
                    itemName={
                        trainingToDelete
                            ? `trening ${dayjs(trainingToDelete.startTime).format('HH:mm')} - ${dayjs(trainingToDelete.endTime).format('HH:mm')}`
                            : ''
                    }
                />
            </div>
        </OwnerLayout>
    );
};

const OwnerCalendar = () => {
    return (
        <OwnerCalendarProvider>
            <OwnerCalendarContent />
        </OwnerCalendarProvider>
    );
};

export default OwnerCalendar;